// Official Newsroom — first-party editorial from Buddha KA / KAfrequency.
// These are not independent press articles. They are labeled and rendered as
// official statements. Add new entries here — no admin setup required.

import buddhaKaImage from "@/assets/buddha-ka-official.png.asset.json";
import ascendedAndArmedCover from "@/assets/ascended-and-armed-cover.png.asset.json";
import crystallizedMindCover from "@/assets/the-crystallized-mind-cover.png.asset.json";
import innerFlameCover from "@/assets/the-inner-flame-sutra-cover.png.asset.json";
import diamondSutraCover from "@/assets/the-diamond-sutra-cover.png.asset.json";
import { absoluteUrl } from "@/lib/site";

/**
 * `press-release` — a formal first-party announcement, syndicated to the
 * press-release feed and archive. `feature` — editorial context and profiles.
 */
export type NewsKind = "press-release" | "feature";

export interface NewsArticle {
  slug: string;
  title: string;
  /** Short excerpt / meta description. */
  dek: string;
  publishedAt: string; // ISO date (YYYY-MM-DD)
  updatedAt?: string; // ISO date, only when materially revised
  authorName: string;
  category: string;
  /** Defaults to `feature` when omitted. */
  kind?: NewsKind;
  /** Slug of the music release this announcement is about, when applicable. */
  releaseSlug?: string;
  image: { url: string; alt: string };
  body: string[]; // Paragraphs (plain text)
  tags: string[];
  /** Internal destinations relevant to this story. */
  links: { to: string; label: string }[];
}


export const NEWSROOM_DISCLOSURE =
  "This article is published by the official Buddha KA / KAfrequency newsroom. It is a first-party statement, not independent press coverage.";

const BYLINE = "Buddha KA / KAfrequency Editorial";
const PORTRAIT = { url: absoluteUrl(buddhaKaImage.url), alt: "Buddha KA — official portrait" };

export const NEWS_ARTICLES: NewsArticle[] = [
  {
    slug: "the-diamond-sutra-out-now-august-19-2026",
    title: "The Diamond Sutra by Buddha KA Is Out Now",
    dek: "Buddha KA released The Diamond Sutra, a 17-track KAfrequency album, on August 19, 2026.",
    publishedAt: "2026-08-19",
    authorName: BYLINE,
    category: "Release",
    kind: "press-release",
    releaseSlug: "the-diamond-sutra",
    image: { url: absoluteUrl(diamondSutraCover.url), alt: "The Diamond Sutra — official album cover artwork by Buddha KA" },
    tags: ["release", "diamond sutra", "kafrequency"],
    links: [
      { to: "/music/the-diamond-sutra", label: "The Diamond Sutra album page" },
      { to: "/music", label: "the KAfrequency music catalog" },
      { to: "/press/the-diamond-sutra", label: "the official press kit" },
    ],
    body: [
      "The Diamond Sutra, a seventeen-track album by Buddha KA, was released on August 19, 2026. It is published first-party through KAfrequency, the artist-owned ecosystem Buddha KA founded.",
      "The album page at buddhaka.com/music/the-diamond-sutra carries the full track listing, the cover artwork, and a dedicated editorial page for each of the seventeen songs.",
      "The record is written inside KA Flow, Buddha KA's own framework joining hip-hop, soul and R&B, spoken wisdom, and spiritual science, and continues the sutra sequence within the KAfrequency catalog.",
      "Additional distribution details will be published here as they are confirmed. Nothing beyond the title, the seventeen-track listing, and the August 19, 2026 release date should be treated as announced.",
    ],
  },
  {
    slug: "buddha-ka-announces-the-diamond-sutra-august-19-2026",
    title: "Buddha KA Announces The Diamond Sutra for August 19, 2026",
    dek: "Buddha KA has announced The Diamond Sutra, a forthcoming KAfrequency project dated August 19, 2026.",
    publishedAt: "2026-07-27",
    authorName: BYLINE,
    category: "Release Announcement",
    kind: "press-release",
    releaseSlug: "the-diamond-sutra",
    image: { url: absoluteUrl(crystallizedMindCover.url), alt: "KAfrequency cover artwork from the sutra cycle" },
    tags: ["release", "diamond sutra", "kafrequency"],
    links: [
      { to: "/music/the-diamond-sutra", label: "The Diamond Sutra release page" },
      { to: "/music", label: "the KAfrequency music catalog" },
      { to: "/books", label: "the written works" },
    ],
    body: [
      "Buddha KA has announced The Diamond Sutra, a forthcoming KAfrequency project scheduled for August 19, 2026. At this time the announced title and the announced date are the confirmed facts; track list, features, and distribution details have not been published.",
      "The project continues the sutra sequence inside the KAfrequency catalog — a body of work Buddha KA writes as instruction rather than entertainment. Earlier titles in the catalog remain available through the artist-owned ecosystem on this site.",
      "Buddha KA works independently across the whole path: writing, composing, delivering the vocal performance, and architecting the systems the music sits inside. The Diamond Sutra is being built the same way.",
      "Further details will be published here as they are confirmed. Nothing beyond the title and the August 19, 2026 date should be treated as announced.",
    ],
  },
  {
    slug: "buddha-ka-introduces-ka-flow",
    title: "Buddha KA Introduces KA Flow, a Spiritual Music Framework",
    dek: "KA Flow is Buddha KA's own creative framework — hip-hop, soul and R&B, spoken wisdom, spiritual science, and cinematic world-building in one lane.",
    publishedAt: "2026-07-25",
    authorName: BYLINE,
    category: "Creative Framework",
    kind: "press-release",
    image: { url: absoluteUrl(ascendedAndArmedCover.url), alt: "Ascended and Armed — KAfrequency cover artwork" },
    tags: ["ka flow", "hip-hop", "spiritual science"],
    links: [
      { to: "/music", label: "the music catalog" },
      { to: "/about", label: "the Buddha KA biography" },
    ],
    body: [
      "Buddha KA has publicly named the creative framework behind his music: KA Flow. KA Flow is his own descriptive framework for the lane he writes in — it is not an external genre classification or a certification issued by any industry body.",
      "The framework combines hip-hop, soul and R&B, spoken wisdom, spiritual science, and cinematic world-building into a single musical language. Bars carry instruction, cadence carries breath, and arrangement carries structure.",
      "KA Flow is written to hold philosophy without softening it. The aim is not a subgenre of \"spiritual rap\" but a lane where the rigor of hip-hop and the interior work of contemplative practice occupy the same record.",
      "The framework governs the KAfrequency catalog going forward, including the announced Diamond Sutra project, and describes the work already released under the KAfrequency name.",
    ],
  },
  {
    slug: "inside-kafrequency-creative-ecosystem",
    title: "Inside KAfrequency: The Creative Ecosystem Built by Buddha KA",
    dek: "Music, books, Daily Activations, visual worlds, Academy content, and community — how the artist-owned KAfrequency ecosystem fits together.",
    publishedAt: "2026-07-21",
    authorName: BYLINE,
    category: "Ecosystem",
    kind: "feature",
    image: PORTRAIT,
    tags: ["kafrequency", "ecosystem", "philosophy"],
    links: [
      { to: "/music", label: "music" },
      { to: "/books", label: "books" },
      { to: "/activations", label: "Daily Activations" },
      { to: "/academy", label: "the Academy" },
    ],
    body: [
      "KAfrequency is not a record label or a merchandise brand. It is the artist-owned creative ecosystem founded and architected by Buddha KA, and its parts are built to reinforce each other rather than compete for attention.",
      "The music arm carries the KA Flow framework and the KAfrequency catalog. The written arm carries the systems, including 47 Gates of Consciousness and The Crystallized Mind. The daily practice arm is KAfrequency: Daily Activations — short spoken transmissions published on this site.",
      "The visual arm builds the cinematic world the sound and writing live inside: Kemetic-rooted imagery, gold and obsidian tonality, and portraiture engineered as symbol rather than promotion.",
      "The systems arm is the interactive work — the 47 Gates journey, the Life Journey Map, the Academy learning paths, and the AI-supported study surface — alongside the community and commerce surfaces already live on buddhaka.com.",
      "Every arm shares one canonical source of instruction and one visual identity. That is what ecosystem means here: the same field, rendered in as many mediums as a practitioner needs to actually use it.",
    ],
  },
  {
    slug: "chicago-wisconsin-las-vegas-buddha-ka-journey",
    title: "From Chicago to Wisconsin and Las Vegas: The Buddha KA Journey",
    dek: "Chicago-born, Wisconsin at 13, Las Vegas at 16, then between Chicago, Texas, and Las Vegas — the geography inside the work.",
    publishedAt: "2026-07-18",
    authorName: BYLINE,
    category: "Profile",
    kind: "feature",
    image: PORTRAIT,
    tags: ["biography", "origin", "chicago"],
    links: [
      { to: "/about", label: "the full biography" },
      { to: "/press", label: "the press kit" },
    ],
    body: [
      "Buddha KA — writing under the legal byline Curtis Shores — was born in Chicago, Illinois. He lived in Chicago until age 13, moved to Wisconsin at 13, and moved to Las Vegas at 16. In the years since, he has moved between Chicago, Texas, and Las Vegas.",
      "The geography matters because the work does not pretend to come from nowhere. Chicago's rhythmic literacy, the interior quiet of the Wisconsin years, and the desert clarity of Las Vegas each press into the writing and the sound.",
      "Movement of that kind either builds interior discipline or exposes its absence. That is the perspective the catalog is written from, and it is why the work is framed as practice rather than as a rise story.",
      "Buddha KA continues to work independently across all three regions, writing the books, making the music, recording the daily activations, and building the consciousness systems himself.",
    ],
  },
  {
    slug: "buddha-ka-expands-music-into-daily-activations",
    title: "Buddha KA Expands Music Into Daily Spoken Activations",
    dek: "KAfrequency: Daily Activations extend attention discipline, breath command, and inner structure beyond the songs into a daily spoken practice.",
    publishedAt: "2026-07-15",
    authorName: BYLINE,
    category: "Practice",
    kind: "press-release",
    image: { url: absoluteUrl(innerFlameCover.url), alt: "The Inner Flame Sutra — KAfrequency cover artwork" },
    tags: ["activations", "practice", "daily"],
    links: [
      { to: "/activations", label: "KAfrequency: Daily Activations" },
      { to: "/music", label: "the music catalog" },
    ],
    body: [
      "Buddha KA has extended the KAfrequency project into Daily Activations — short spoken practices published on this site. Each activation carries an intention, a breath instruction, a phrase to hold, and a journal invitation.",
      "The activations move the same themes the music carries — attention discipline, breath command, inner structure, and spiritual science — into a form a practitioner can use inside an ordinary day.",
      "They are not affirmations or motivational content. They are structured practice, one transmission at a time, written in the same voice as the catalog.",
      "The activations are published on buddhaka.com and inside the KAfrequency mobile experience, and they are indexed alongside the music and the written systems so a returning practitioner can move between listening, reading, and daily practice.",
    ],
  },
];

/** Back-compat alias for existing imports. */
export const NEWS = NEWS_ARTICLES;

export function getNews(): NewsArticle[] {
  return [...NEWS_ARTICLES].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
}

export function getNewsArticle(slug: string): NewsArticle | undefined {
  return NEWS_ARTICLES.find((n) => n.slug === slug);
}

/** Back-compat alias. */
export const findNews = getNewsArticle;

/**
 * Newsroom articles that explicitly link to a given internal path.
 *
 * The edge comes only from `links` written into the article itself — nothing
 * is inferred from prose, tags, or title matching.
 */
export function newsLinkingTo(path: string): NewsArticle[] {
  const target = path.replace(/\/+$/, "");
  return getNews().filter((n) => n.links.some((l) => l.to.replace(/\/+$/, "") === target));
}


/** Newest modified (or published) date across the newsroom, ISO YYYY-MM-DD. */
export function latestNewsDate(): string {
  return getNews()
    .map((n) => n.updatedAt ?? n.publishedAt)
    .sort()
    .reverse()[0];
}

/** Formal first-party announcements, newest first. */
export function getPressReleases(): NewsArticle[] {
  return getNews().filter((n) => n.kind === "press-release");
}

/** Editorial features and profiles, newest first. */
export function getFeatures(): NewsArticle[] {
  return getNews().filter((n) => (n.kind ?? "feature") === "feature");
}

/** Newsroom entries published on or after `sinceIso` (inclusive). */
export function newsSince(sinceIso: string): NewsArticle[] {
  return getNews().filter((n) => (n.updatedAt ?? n.publishedAt) >= sinceIso);
}

/** Announcements explicitly tied to a music release slug. */
export function newsForRelease(slug: string): NewsArticle[] {
  return getNews().filter((n) => n.releaseSlug === slug);
}
